import { Element, SelectMetaData, iSelectionElement } from '@vindr/services/apis/element.api';
import { Omit } from '@vindr/utils/helpers/common.helper';
import { BaseElement, BaseElementProps } from '../base';
import { ElementFactory } from '../base/ElementFactory';
import { StructuredReportElement } from '../types';
import isEmpty from 'lodash/isEmpty';

export interface SelectionItem {
  label: string;
  value: string | number;
  isLinked?: boolean;
}

export type SelectionElementProps = Omit<BaseElementProps, 'metadata'> &
  iSelectionElement & {
    label: string;
    placeholder?: string;
    items: SelectMetaData['items'];
    is_editable_value: boolean;
    is_free_text: boolean;
    free_text_value: string | number;
  };

class SelectionElement extends BaseElement {
  public default_value: string | number;
  public label: string;
  public is_show_label: boolean;
  public items: SelectionItem[];
  public placeholder: string;
  public is_editable_value: boolean;
  public is_free_text: boolean;
  private props: SelectionElementProps;
  public elements: StructuredReportElement[];
  public free_text_value: string | number;
  public isLinkedSelect: boolean;

  constructor(props: SelectionElementProps) {
    super(props);
    this.props = props;
    this.level = (props.parent?.level ?? -1) + 1;

    const {
      default_value,
      label,
      is_show_label,
      placeholder,
      is_editable_value,
      is_free_text,
      free_text_value,
      value,
      items,
    } = props || {};

    this.label = label;
    this.is_show_label = is_show_label;
    this.placeholder = placeholder || 'Select';

    const itemList = (items || props?.metadata?.items)
      .map((it) => ({
        label: it.label,
        isLinked: it.isLinked || !!it.linked_id,
        value: it.linked_id || it.value, // if linked_id appear, this mean item is link to an element
      }))
      .filter((it) => it.value !== 'FREE_TEXT_VALUE');

    if (is_free_text) {
      //this variable allow free text item
      itemList.push({
        label: 'Free Text',
        value: 'FREE_TEXT_VALUE',
        isLinked: false,
      });
    }
    this.items = itemList;

    if (props?.metadata?.default_value) {
      // Search if default value is label of some item
      const findItem = this.items.find((it) => it.label === props?.metadata?.default_value);
      if (findItem) this.default_value = findItem.value;
    }
    this.default_value =
      (default_value as string) || this.default_value || props?.metadata?.default_value || null;
    this.value = value || this.default_value || null;

    this.isLinkedSelect = this.items.find((it) => it.value === this.value)?.isLinked;
    this.is_editable_value = is_editable_value;
    this.is_free_text = is_free_text;
    this.free_text_value = free_text_value;
    this.elements = (props?.elements || []).map((element) =>
      ElementFactory.createElement({
        ...element,
        parent: this,
        level: this.level + 1,
      })
    );
  }

  public setValue(value: string) {
    this.value = value;
    this.isLinkedSelect = this.items.find((it) => it.value === value)?.isLinked;
    return this;
  }

  public setElements(elements: Element[]) {
    if (!Array.isArray(elements)) {
      console.error('Elements should be an array', elements);
      return this;
    }

    this.elements = elements.map((element) =>
      ElementFactory.createElement({
        ...element,
        parent: this,
        level: this.level + 1,
      })
    );

    return this;
  }

  public setHighlight(val: boolean): void {
    this.highlight = val;
    // If need hightlight, just need highlight this, else should dehighlight every childrens
    if (this.elements) {
      for (const child of this.elements) {
        if (typeof child?.setHighlight === 'function') {
          child.setHighlight(val);
        } else {
          console.error('Not have setHighlight function on child', child);
        }
      }
    }
  }

  public render(): string {
    if (this.is_hidden) return '';
    const label = this.is_show_label && !isEmpty(this.value) ? this.label : '';
    let content = '';
    if (this.isLinkedSelect) {
      const selectedElement = this.elements.find((it) => it?.id === this.value);
      if (selectedElement instanceof BaseElement) {
        content = selectedElement.render();
      }
    } else if (this.value === 'FREE_TEXT_VALUE') {
      content = String(this.free_text_value) || '';
    } else {
      content = this.value || '';
    }

    return this.withHighlight(label + content);
  }

  public toJSON() {
    const json = Omit(this, ['parent', 'props', 'highlight']);
    json.elements = this.elements.map((child) =>
      typeof child?.toJSON === 'function' ? child.toJSON() : child
    );
    return json;
  }
}

export default SelectionElement;
